/**
 * WebSocket Manager
 * 
 * Manages WebSocket connections for streaming agent execution.
 * Handles reconnection, message buffering, and event dispatching.
 *
 * In local mode: connects directly to ws://localhost:8000.
 * In cloud mode: routes through the AWS backend proxy at /ws/proxy/*.
 */

import { getAuthToken } from '../utils/auth';

const DEPLOYMENT_MODE = process.env.REACT_APP_DEPLOYMENT_MODE || 'local';
const CLOUD_API_URL = process.env.REACT_APP_CLOUD_API_URL || '';

function getWsBase() {
    if (DEPLOYMENT_MODE === 'cloud') {
        const wsUrl = CLOUD_API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
        return `${wsUrl}/ws/proxy`;
    }
    return process.env.REACT_APP_WS_URL || 'ws://localhost:8000';
}

function getWsUrl(path) {
    const base = getWsBase();
    const url = `${base}${path}`;
    if (DEPLOYMENT_MODE === 'cloud') {
        const token = getAuthToken();
        if (token) {
            return `${url}?token=${encodeURIComponent(token)}`;
        }
    }
    return url;
}

/**
 * Workflow WebSocket Manager (for Agent Designer)
 *
 * Supports automatic reconnection for multi-agent workflows:
 * - Tracks the last received event sequence number
 * - On unexpected disconnect, reconnects and replays missed events
 * - Exposes onReconnecting / onReconnected callbacks for UI feedback
 */
export class WorkflowWebSocket {
    constructor(options = {}) {
        this.url = getWsUrl('/ws/workflow/stream');
        this.ws = null;
        this.isConnected = false;

        // Reconnection state
        this._isWorkflowActive = false;
        this._userInitiatedClose = false;
        this._isReconnecting = false;
        this._reconnectAttempts = 0;
        this._maxReconnectAttempts = options.maxReconnectAttempts || 5;
        this._reconnectTimer = null;
        this._lastEventSeq = -1;
        this._projectId = null;

        // Event handlers
        this.onOpen = options.onOpen || (() => { });
        this.onMessage = options.onMessage || (() => { });
        this.onError = options.onError || (() => { });
        this.onClose = options.onClose || (() => { });
        this.onWorkflowStart = options.onWorkflowStart || (() => { });
        this.onWorkflowEvent = options.onWorkflowEvent || (() => { });
        this.onWorkflowComplete = options.onWorkflowComplete || (() => { });
        this.onWorkflowError = options.onWorkflowError || (() => { });

        // Message management callbacks
        this.onMessageCreated = options.onMessageCreated || (() => { });
        this.onCacheInvalidate = options.onCacheInvalidate || (() => { });

        // Task event callback (real-time task updates from backend)
        this.onTaskEvent = options.onTaskEvent || (() => { });

        // Reconnection callbacks
        this.onReconnecting = options.onReconnecting || (() => { });
        this.onReconnected = options.onReconnected || (() => { });

        // Interrupt callback (human-in-the-loop design questionnaire)
        this.onInterrupt = options.onInterrupt || (() => { });

        // Credits settled after usage recording
        this.onCreditsSpent = options.onCreditsSpent || (() => { });

        // Project thumbnail updated after the run
        this.onThumbnailUpdated = options.onThumbnailUpdated || (() => { });

        // Event batching: streaming events are queued and processed in batches
        // per animation frame to avoid blocking the main thread.
        this._eventQueue = [];
        this._eventQueueIndex = 0;  // index-based dequeue (avoids O(n) shift)
        this._flushing = false;
        this._batchRaf = null;
        this._BATCH_SIZE = 80;
    }

    connect() {
        return new Promise((resolve, reject) => {
            try {
                this.ws = new WebSocket(this.url);

                this.ws.onopen = () => {
                    this.isConnected = true;
                    this.onOpen();
                    resolve();
                };

                this.ws.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        this.handleMessage(data);
                    } catch (e) {
                        console.error('WebSocket message parse error:', e);
                    }
                };

                this.ws.onerror = (error) => {
                    this.onError(error);
                    reject(error);
                };

                this.ws.onclose = (event) => {
                    this.isConnected = false;
                    this._handleClose(event);
                };

            } catch (error) {
                reject(error);
            }
        });
    }

    _handleClose(event) {
        if (this._userInitiatedClose) {
            // User pressed Stop — don't reconnect
            this.onClose(event);
            return;
        }

        if (this._isWorkflowActive && !this._isReconnecting) {
            // Unexpected disconnect during active workflow — try to reconnect
            console.log('[WorkflowWS] Unexpected disconnect during workflow, attempting reconnect...');
            this._attemptReconnect();
            return;
        }

        if (!this._isReconnecting) {
            this.onClose(event);
        }
    }

    _attemptReconnect() {
        if (this._reconnectAttempts >= this._maxReconnectAttempts) {
            console.warn(`[WorkflowWS] Max reconnect attempts (${this._maxReconnectAttempts}) reached`);
            this._isReconnecting = false;
            this._isWorkflowActive = false;
            this.onClose(new CloseEvent('close'));
            return;
        }

        this._isReconnecting = true;
        this._reconnectAttempts++;

        const delay = Math.min(1000 * Math.pow(2, this._reconnectAttempts - 1), 8000);
        console.log(
            `[WorkflowWS] Reconnect attempt ${this._reconnectAttempts}/${this._maxReconnectAttempts} in ${delay}ms ` +
            `(lastSeq=${this._lastEventSeq})`
        );

        this.onReconnecting(this._reconnectAttempts, this._maxReconnectAttempts);

        this._reconnectTimer = setTimeout(() => {
            this._doReconnect();
        }, delay);
    }

    async _doReconnect() {
        try {
            this.ws = new WebSocket(this.url);

            this.ws.onopen = () => {
                this.isConnected = true;
                // Send reconnect payload instead of workflow+task
                const payload = {
                    reconnect: {
                        projectId: this._projectId,
                        lastEventSeq: this._lastEventSeq,
                    }
                };
                console.log('[WorkflowWS] Sending reconnect payload:', payload);
                this.ws.send(JSON.stringify(payload));
            };

            this.ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);

                    // Handle reconnection acknowledgement
                    if (data.type === 'workflow_reconnected') {
                        console.log('[WorkflowWS] Reconnected successfully');
                        this._isReconnecting = false;
                        this._reconnectAttempts = 0;
                        this.onReconnected();
                        return;
                    }

                    this.handleMessage(data);
                } catch (e) {
                    console.error('WebSocket message parse error:', e);
                }
            };

            this.ws.onerror = (error) => {
                console.error('[WorkflowWS] Reconnect error:', error);
                // Will trigger onclose which will retry
            };

            this.ws.onclose = (event) => {
                this.isConnected = false;
                if (this._isReconnecting || this._isWorkflowActive) {
                    // Reconnection socket died — try again
                    this._attemptReconnect();
                } else {
                    this.onClose(event);
                }
            };

        } catch (error) {
            console.error('[WorkflowWS] Failed to create reconnect socket:', error);
            this._attemptReconnect();
        }
    }

    handleMessage(data) {
        if (typeof data.seq === 'number' && data.seq >= 0) {
            this._lastEventSeq = data.seq;
        }

        this.onMessage(data);

        // Streaming events are batched and processed per animation frame
        // to prevent flooding the main thread. Control/lifecycle events
        // are processed immediately but flush the queue first for ordering.
        if (data.type === 'agent_event' || data.type === 'multiagent_event') {
            this._eventQueue.push(data.event);
            this._scheduleBatch();
            return;
        }

        // Control event — flush pending streaming events first, then handle
        this._flushEventQueue();

        switch (data.type) {
            case 'workflow_start':
                this.onWorkflowStart(data);
                break;
            case 'workflow_complete':
                this._isWorkflowActive = false;
                this.onWorkflowComplete();
                break;
            case 'workflow_error':
                this.onWorkflowError(data);
                break;
            case 'error':
                this.onError(new Error(data.message));
                break;
            case 'message_created':
                this.onMessageCreated(data.message);
                break;
            case 'cache_invalidate':
                this.onCacheInvalidate(data.project_id, data.reason);
                break;
            case 'task_event':
                this.onTaskEvent(data.data);
                break;
            case 'interrupt':
                console.log('[WorkflowWS] Received interrupt event:', JSON.stringify(data).slice(0, 500));
                try {
                    this.onInterrupt(data);
                } catch (e) {
                    console.error('[WorkflowWS] Error in onInterrupt handler:', e);
                }
                break;
            case 'handoff_warning':
                console.warn('[WorkflowWS] Handoff warning:', data.message);
                break;
            case 'credits_spent':
                try {
                    this.onCreditsSpent(data);
                } catch (e) {
                    console.error('[WorkflowWS] Error in onCreditsSpent handler:', e);
                }
                break;
            case 'thumbnail_updated':
                try {
                    this.onThumbnailUpdated(data);
                } catch (e) {
                    console.error('[WorkflowWS] Error in onThumbnailUpdated handler:', e);
                }
                break;
            case 'thumbnail_skipped':
                console.log('[WorkflowWS] Thumbnail skipped:', data.reason);
                break;
            case 'heartbeat':
                break;
        }
    }

    _scheduleBatch() {
        if (this._batchRaf) return;
        this._batchRaf = requestAnimationFrame(() => {
            this._batchRaf = null;
            this._processEventBatch();
        });
    }

    _processEventBatch() {
        let count = 0;
        while (this._eventQueueIndex < this._eventQueue.length && count < this._BATCH_SIZE) {
            const event = this._eventQueue[this._eventQueueIndex++];
            try {
                this.onWorkflowEvent(event);
            } catch (e) {
                console.error('[WorkflowWS] Error processing batched event:', e);
            }
            count++;
        }
        if (this._eventQueueIndex > 1000) {
            this._eventQueue = this._eventQueue.slice(this._eventQueueIndex);
            this._eventQueueIndex = 0;
        }
        if (this._eventQueueIndex < this._eventQueue.length) {
            this._scheduleBatch();
        }
    }

    _flushEventQueue() {
        if (this._batchRaf) {
            cancelAnimationFrame(this._batchRaf);
            this._batchRaf = null;
        }
        if (this._flushing) return;
        this._flushing = true;

        const processBatch = () => {
            let count = 0;
            while (this._eventQueueIndex < this._eventQueue.length && count < this._BATCH_SIZE) {
                const event = this._eventQueue[this._eventQueueIndex++];
                try {
                    this.onWorkflowEvent(event);
                } catch (e) {
                    console.error('[WorkflowWS] Error processing queued event:', e);
                }
                count++;
            }
            if (this._eventQueueIndex > 1000) {
                this._eventQueue = this._eventQueue.slice(this._eventQueueIndex);
                this._eventQueueIndex = 0;
            }
            if (this._eventQueueIndex < this._eventQueue.length) {
                requestAnimationFrame(processBatch);
            } else {
                this._flushing = false;
            }
        };
        processBatch();
    }

    async execute(workflow, task) {
        if (!this.isConnected) {
            await this.connect();
        }

        // Track project ID for reconnection
        this._projectId = task?.project_id || null;
        this._isWorkflowActive = true;
        this._userInitiatedClose = false;
        this._reconnectAttempts = 0;
        this._lastEventSeq = -1;

        this.send({ workflow, task });
    }

    /**
     * Resume an active workflow after a page refresh.
     * Connects and sends a reconnect payload with lastEventSeq=-1
     * to replay all buffered events from the backend.
     */
    async resume(projectId) {
        this._projectId = projectId;
        this._isWorkflowActive = true;
        this._userInitiatedClose = false;
        this._reconnectAttempts = 0;
        this._lastEventSeq = -1;

        await this._doReconnect();
    }

    send(data) {
        if (!this.isConnected || !this.ws) {
            throw new Error('WebSocket not connected');
        }
        this.ws.send(JSON.stringify(data));
    }

    sendInterruptResponse(interruptId, response) {
        this.send({
            type: 'interrupt_response',
            interrupt_id: interruptId,
            response: response,
        });
    }

    close() {
        this._userInitiatedClose = true;
        this._isWorkflowActive = false;
        this._isReconnecting = false;

        if (this._batchRaf) {
            cancelAnimationFrame(this._batchRaf);
            this._batchRaf = null;
        }
        this._eventQueue.length = 0;
        this._eventQueueIndex = 0;
        this._flushing = false;

        if (this._reconnectTimer) {
            clearTimeout(this._reconnectTimer);
            this._reconnectTimer = null;
        }

        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        this.isConnected = false;
    }
}

export default {
    WorkflowWebSocket,
};
